/** Indian digit grouping — 3,52,877.10, not 352,877.10. */
export function inr(n: number | string | null | undefined, decimals = 2): string {
  const v = Number(n ?? 0)
  if (!isFinite(v)) return '0.00'
  const neg = v < 0
  const [whole, frac] = Math.abs(v).toFixed(decimals).split('.')
  let out: string
  if (whole.length <= 3) out = whole
  else out = whole.slice(0, -3).replace(/\B(?=(\d{2})+(?!\d))/g, ',') + ',' + whole.slice(-3)
  return (neg ? '-' : '') + out + (decimals > 0 ? '.' + frac : '')
}

export const rupees = (n: number | null | undefined) => `₹ ${inr(n)}`

/** Quantities: hide meaningless trailing zeros, keep genuine decimals. */
export function qty(n: number | string | null | undefined): string {
  const v = Number(n ?? 0)
  if (!isFinite(v)) return '0'
  const r = Math.round(v * 1000) / 1000
  return Number.isInteger(r) ? inr(r, 0) : inr(r, 3).replace(/0+$/, '').replace(/\.$/, '')
}

/**
 * Today's date (or d's) as YYYY-MM-DD in the browser's own time - India's for
 * everyone using this. NOT toISOString(), which is UTC: between midnight and
 * 05:30 IST it gives YESTERDAY's date, and a local midnight (1 April) comes
 * out as the day before (31 March). Found 04-11-2026.
 */
export function localIso(d: Date = new Date()): string {
  const p = (x: number) => String(x).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

export function ddmmyyyy(d: string | Date | null | undefined): string {
  if (!d) return '—'
  const date = typeof d === 'string' ? new Date(d) : d
  if (isNaN(date.getTime())) return '—'
  const p = (x: number) => String(x).padStart(2, '0')
  return `${p(date.getDate())}-${p(date.getMonth() + 1)}-${date.getFullYear()}`
}

export function dateTime(d: string | Date | null | undefined): string {
  if (!d) return '—'
  const date = typeof d === 'string' ? new Date(d) : d
  if (isNaN(date.getTime())) return '—'
  const p = (x: number) => String(x).padStart(2, '0')
  return `${ddmmyyyy(date)} ${p(date.getHours())}:${p(date.getMinutes())}`
}

export function relativeTime(d: string | Date | null | undefined): string {
  if (!d) return '—'
  const date = typeof d === 'string' ? new Date(d) : d
  const secs = Math.round((Date.now() - date.getTime()) / 1000)
  if (secs < 60) return 'just now'
  if (secs < 3600) return `${Math.floor(secs / 60)} min ago`
  if (secs < 86400) return `${Math.floor(secs / 3600)} hr ago`
  if (secs < 604800) return `${Math.floor(secs / 86400)} d ago`
  return ddmmyyyy(date)
}

const ONES = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten',
  'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen']
const TENS = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety']

function under1000(n: number): string {
  let out = ''
  if (n >= 100) {
    out += ONES[Math.floor(n / 100)] + ' Hundred'
    n %= 100
    if (n > 0) out += ' '
  }
  if (n >= 20) {
    out += TENS[Math.floor(n / 10)]
    if (n % 10) out += ' ' + ONES[n % 10]
  } else if (n > 0) out += ONES[n]
  return out
}

/** Indian numbering system, worded exactly as on the approved PO template. */
export function amountInWords(amount: number): string {
  if (!isFinite(amount)) return ''
  let rupeesPart = Math.floor(Math.abs(amount))
  let paise = Math.round((Math.abs(amount) - rupeesPart) * 100)
  if (paise === 100) { rupeesPart += 1; paise = 0 }

  const crore = Math.floor(rupeesPart / 10000000)
  const lakh = Math.floor((rupeesPart % 10000000) / 100000)
  const thousand = Math.floor((rupeesPart % 100000) / 1000)
  const rest = rupeesPart % 1000

  let parts = ''
  if (crore) parts += under1000(crore) + ' Crore '
  if (lakh) parts += under1000(lakh) + ' Lakh '
  if (thousand) parts += under1000(thousand) + ' Thousand '
  if (rest) parts += under1000(rest) + ' '
  parts = parts.replace(/\s+/g, ' ').trim() || 'Zero'

  return paise > 0
    ? `INR ${parts} Rupees and ${under1000(paise).trim()} Paise Only`
    : `INR ${parts} Rupees Only`
}
